// src/components/desk/PersonModal.jsx — the person view: one party, both sides (demands +
// owned/listed properties) — INTENT_LABEL_KEYS, demandLine, listingLine, PhonePersonHeader,
// PartyDuplicates and PersonModal itself — extracted VERBATIM from IntakeTab.jsx
// (DESK_MODE_REWRITE_PLAN §3 decomposition, behavior-preserving). PartyDuplicates and
// PhonePersonHeader are also rendered outside this modal, so they are exported alongside it.

import { useEffect, useState } from 'react'
import { t, tLog } from '../../lib/i18n.js'
import { Icon, IconText } from '../Icon.jsx'
import { card, btn, Badge, BtnLabel, COUNTER, Money, Modal } from './primitives.jsx'
import { ContactPill } from './ContactPill.jsx'
import { CooperationChips, coopIntentOf } from './CooperationChips.jsx'
import { CooperationModal } from './CooperationModal.jsx'
import { findPartyCandidates, getParty, getPartyDuplicates, listCooperations, recordPartyVerdict } from '../../db/core.js'
import { supabase } from '../../db/index.js'
import { intentLabel, kindLabel } from '../../lib/labels.js'
import { fmtPhone } from '../../lib/phone.js'

// ─── Person view — one party, both sides (demands + owned/listed properties) ───
// The answer to "this same person is a client AND selling their place AND has two different
// searches" — party is one identity; demands and listings are evidence layered on it (see
// party_overview RPC, migration 0042). Reached from a demand's edit modal (its person header)
// so the agent lands on the full human, not just one of their threads. Demands here are
// clickable → jump into that specific demand's editor via onOpenDemand.
// (INTENT_LABEL_KEYS retired 2026-07-23 → src/lib/labels.js — the ONE display dictionary;
//  property_category also passed raw here before, same fix.)
function demandLine(d, districts) {
  const c = d.criteria || {}
  const bits = [intentLabel(d.intent, 'demand'), kindLabel(d.property_category)].filter(Boolean)
  const distAll = [...(c.district_must_codes || []), ...(c.district_codes || [])]
  if (distAll.length) {
    const names = distAll.slice(0, 2).map(code => { const dd = districts.find(x => x.code === code); return dd ? (dd.name_en || dd.name_ka || code) : code })
    bits.push(names.join(', ') + (distAll.length > 2 ? ` +${distAll.length - 2}` : ''))
  }
  if (c.price_max != null) bits.push(tLog('desk_person_up_to_price', { price: c.price_max, currency: c.currency || '' }).trim())
  return bits.join(' · ')
}
function listingLine(l) {
  const pr = l.property || {}
  const bits = [pr.kind, pr.address].filter(Boolean)
  const size = [pr.rooms ? tLog('desk_entry_rooms_abbrev', { rooms: pr.rooms }) : null, pr.area_sqm ? tLog('desk_entry_area_abbrev', { area: pr.area_sqm }) : null].filter(Boolean).join(', ')
  if (size) bits.push(size)
  return bits.join(' · ')
}

// ── Phone→person search header (2026-07-19, owner-approved) ──────────────────
// An agent typing a PHONE into the search box is asking "who is this and what's
// our relationship?", not "which listings mention this string" (KNOWLEDGE_MODEL
// §0 decision-first). Typing the phone is proof of possession (§1.6), so
// identity may be shown before core_visibility.js exists — everything else on
// the strip (role counts, agent badge) is already agency-visible. Reuses
// find_party_candidates (0123) — no new endpoint; PersonModal (opened via
// onOpenPerson) does the cluster aggregation (0127). Multiple rows on one
// phone are the resolve_party capture design (0027) — shown as-is with a
// possible-duplicates hint; the PersonModal dup card is where they get judged.
// Self-contained BY AGREEMENT with the intake/matching thread (B48 edits
// PersonModal internals; this component is caller-side only).
function PhonePersonHeader({ phones, onOpenPerson }) {
  const [rows, setRows] = useState([])       // [{ phone, candidates }]
  const phoneKey = (phones || []).join(',')
  useEffect(() => {
    let gone = false
    const ps = (phones || []).slice(0, 3)    // sanity cap; queries carry 1 phone in practice
    if (!ps.length) { setRows([]); return }
    Promise.all(ps.map(p =>
      findPartyCandidates({ phone: p }).then(r => ({ phone: p, candidates: r.candidates || [] })).catch(() => ({ phone: p, candidates: [] }))
    )).then(out => { if (!gone) setRows(out.filter(r => r.candidates.length)) })
    return () => { gone = true }
  }, [phoneKey]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!rows.length) return null
  return (
    <div className="mb-2 space-y-1">
      {rows.map(({ phone, candidates }) => {
        const head = candidates[0]
        const extras = candidates.length - 1
        return (
          <div key={phone} className="flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-1.5 text-sm">
            <span className="text-muted-foreground"><Icon name="phone" /> {fmtPhone(phone)}</span>
            <span className="font-medium">{head.display_name || t('desk_person_unnamed')}</span>
            {head.is_agent && <Badge label={t('desk_phone_header_agent')} />}
            <span className="text-xs text-muted-foreground">
              {head.listings_count > 0 && <><Icon name="house" /> {head.listings_count} </>}
              {head.requirements_count > 0 && <><Icon name="search" /> {head.requirements_count} </>}
              {head.cooperations_count > 0 && <><Icon name="handshake" /> {head.cooperations_count}</>}
              {!head.listings_count && !head.requirements_count && !head.cooperations_count && t('desk_phone_header_no_activity')}
            </span>
            {extras > 0 && (
              <span className="text-xs text-amber-600 dark:text-amber-400" title={t('desk_party_dup_title')}>
                +{extras} {t('desk_phone_header_dup_records')}
              </span>
            )}
            <button className="text-xs text-primary underline decoration-dotted ml-auto shrink-0"
              onClick={() => onOpenPerson(head.party_id)}>
              {t('desk_phone_header_open')}
            </button>
          </div>
        )
      })}
    </div>
  )
}

// ── Party possible-duplicates card (core 0125, mirrors PropertyDuplicates) ────
// A verdict is EVIDENCE, never a merge: record_party_verdict maintains
// same_as/different_from edges; party_cluster() is the only collapse surface.
// Judging requires an agent identity; buttons are privileged-gated (managers
// act on merge candidates — VISIBILITY.md §3 party).
function PartyDuplicates({ partyId, agentId, privileged = false }) {
  const [data, setData] = useState(null)   // { pending, cluster }
  const [err, setErr] = useState(null)
  const [busy, setBusy] = useState(null)
  const [refresh, setRefresh] = useState(0)
  useEffect(() => {
    let gone = false
    getPartyDuplicates(partyId)
      .then(d => { if (!gone) { setData(d); setErr(null) } })
      .catch(e => { if (!gone) setErr(e.message) })
    return () => { gone = true }
  }, [partyId, refresh])

  async function judge(other, verdict) {
    // Dual identity (mirrors DedupSection): a bridged agent judges via agentId;
    // an admin with no agents-table row (e.g. gigi) judges via their own scheduler
    // session token, server-reverified. Without either there's no judge → block.
    let adminToken = null
    if (!agentId) {
      if (!privileged) { setErr(t('desk_dup_no_identity')); return }
      const { data: { session: fresh } } = await supabase.auth.getSession()
      adminToken = fresh?.access_token
      if (!adminToken) { setErr(t('desk_dup_no_identity')); return }
    }
    setBusy(other)
    try {
      await recordPartyVerdict({ partyA: partyId, partyB: other, verdict, agentId, adminToken })
      setRefresh(r => r + 1)
    } catch (e) { setErr(e.message) } finally { setBusy(null) }
  }

  const pending = data?.pending || []
  const cluster = data?.cluster || []
  if (data && !err && !pending.length && !cluster.length) return null
  const line = p => [p.display_name, p.primary_phone, p.primary_email].filter(Boolean).join(' · ') || p.id

  return (
    <div className={card}>
      <p className="text-xs font-semibold text-muted-foreground mb-2">{t('desk_party_dup_title')}</p>
      {err && <p className="text-sm text-destructive"><Icon name="alert-triangle" /> {err}</p>}
      {!data && !err && <p className="text-xs text-muted-foreground">{t('desk_loading')}</p>}
      {pending.length > 0 && (
        <ul className="space-y-2">
          {pending.map(c => (
            <li key={c.candidate_id} className="border rounded p-2 text-xs">
              <div className="font-medium">{line(c.other_party || {})}</div>
              <div className="text-muted-foreground mt-0.5">
                {Object.keys(c.signals || {}).map(s => s === 'phone_match' ? '📞 ' + t('desk_party_dup_same_phone') : s === 'email_match' ? '✉️ ' + t('desk_party_dup_same_email') : s).join(' · ')}
              </div>
              {privileged && (
                <div className="flex gap-1 mt-1.5">
                  <button className={btn('ghost')} disabled={busy === c.other_party?.id} onClick={() => judge(c.other_party.id, 'same')}><IconText>{t('desk_dup_verdict_same')}</IconText></button>
                  <button className={btn('ghost')} disabled={busy === c.other_party?.id} onClick={() => judge(c.other_party.id, 'different')}><IconText>{t('desk_dup_verdict_different')}</IconText></button>
                  <button className={btn('ghost')} disabled={busy === c.other_party?.id} onClick={() => judge(c.other_party.id, 'unsure')}>{t('desk_dup_verdict_unsure')}</button>
                  {busy === c.other_party?.id && <span className="text-muted-foreground self-center"><Icon name="hourglass" /></span>}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {cluster.length > 0 && (
        <div className="mt-2 text-xs">
          <div className="font-medium">{t('desk_party_dup_cluster')}</div>
          <ul className="text-muted-foreground">
            {cluster.map(m => <li key={m.id}>• {line(m)}</li>)}
          </ul>
        </div>
      )}
    </div>
  )
}

function PersonModal({ partyId, districts, onClose, onOpenDemand, onAddRequest = null, privileged = false, agentId = null }) {
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [data, setData] = useState(null)
  const [coops, setCoops] = useState([])
  const [coopModal, setCoopModal] = useState(null) // { propertyId, propertyLabel, cooperationId? }

  function reloadCoops() {
    listCooperations({ contactPartyId: partyId }).then(setCoops).catch(() => {})
  }
  function reloadParty() {
    return getParty(partyId).then(setData).catch(e => setErr(e.message))
  }

  useEffect(() => {
    let cancelled = false
    // review 2026-09-23: a new partyId starts clean — an earlier error / loaded state no longer sticks.
    setErr(null); setLoading(true)
    getParty(partyId).then(d => { if (!cancelled) { setData(d); setLoading(false) } })
      .catch(e => { if (!cancelled) { setErr(e.message); setLoading(false) } })
    reloadCoops()
    return () => { cancelled = true }
  }, [partyId])

  return (
    <Modal title={t('desk_person_modal_title')} onClose={onClose}>
      {loading ? (
        <p className="text-sm text-muted-foreground py-6 text-center">{t('desk_loading')}</p>
      ) : err ? (
        <p className="text-sm text-destructive py-6 text-center">{tLog('desk_couldnt_load', { err })}</p>
      ) : (
        <div className="space-y-4">
          {/* Identity */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-lg">{data.kind === 'company' ? <Icon name="building" size={18} /> : <Icon name="user-check" />}</span>
            <span className="text-base font-semibold">{data.name || t('desk_person_unnamed')}</span>
            {/* Contact lives in the footer bottom-left, NOT here — the ContactReveal rule
                (same place, same look, so agents build the habit). See the footer below. */}
            {/* Cross-agent masking (2026-07-16, same as ClientEditModal): another agent's
                client hides THEIR contact — the representative is who you call. */}
            {data.agent?.name && <span className="text-sm text-muted-foreground"><IconText>{tLog('desk_client_owner_agent', { name: data.agent.name })}</IconText></span>}
            {data.contact_masked && data.agent?.phone && (
              <a href={`tel:${data.agent.phone}`} className="text-sm text-primary"><Icon name="phone" /> {data.agent.phone}</a>
            )}
            {/* cluster aggregation (core 0127): this view combines confirmed-duplicate rows */}
            {data.cluster_size > 1 && (
              <span className={`${COUNTER} bg-muted text-muted-foreground`} title={t('desk_party_combined_hint')}>
                {t('desk_party_combined').replace('{n}', data.cluster_size)}
              </span>
            )}
          </div>

          {/* Demand side */}
          <div className={card}>
            <div className="flex items-center justify-between mb-2 gap-2">
              <p className="text-xs font-semibold text-muted-foreground">
                {tLog('desk_person_looking_for', { n: data.demands.length })}
              </p>
              {onAddRequest && (
                /* B48: a NEW demand for THIS person — buy/rent/short_term, any number, each
                   its own request; binds to data.id → person.party_id, so no duplicate person. */
                <button onClick={() => onAddRequest({ id: data.id || partyId, name: data.name, phone: data.phone, email: data.email })}
                  className="text-xs text-primary hover:underline shrink-0">＋ {t('desk_person_add_request')}</button>
              )}
            </div>
            {data.demands.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('desk_person_no_active_searches')}</p>
            ) : (
              <div className="space-y-1.5">
                {data.demands.map(d => (
                  <button key={d.id} onClick={() => onOpenDemand(d.id)}
                    className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-accent transition-colors">
                    <span className="shrink-0"><Icon name="search" /></span>
                    <span className="text-sm flex-1 min-w-0 truncate">{demandLine(d, districts)}</span>
                    <Badge label={t(`desk_client_status_${d.status}`) || d.status} variant={d.status === 'active' ? 'green' : d.status === 'closed' ? 'red' : 'default'} />
                    <span className="text-xs text-muted-foreground shrink-0"><Icon name="pencil" /></span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Supply side */}
          <div className={card}>
            <p className="text-xs font-semibold text-muted-foreground mb-2">
              {tLog('desk_person_owns_selling', { n: data.listing_count })}
            </p>
            {(!data.listings || data.listings.length === 0) ? (
              <p className="text-sm text-muted-foreground">{t('desk_person_no_listings')}</p>
            ) : (
              <div className="space-y-1.5">
                {data.listings.map(l => (
                  <div key={l.listing_id} className="flex items-center gap-2 px-2 py-1.5">
                    <span className="shrink-0"><Icon name="house" /></span>
                    <span className="text-sm flex-1 min-w-0 truncate">{listingLine(l)}</span>
                    {l.price != null && <Money amount={l.price} currency={l.currency} className="text-xs text-muted-foreground shrink-0 tabular-nums" />}
                    <Badge label={(l.status && t(`desk_status_val_${l.status}`)) || l.status || '—'} variant={l.status === 'active' ? 'green' : 'default'} />
                    {l.source_url && <a href={l.source_url} target="_blank" rel="noreferrer" className="text-xs text-primary shrink-0" onClick={e => e.stopPropagation()}><Icon name="external-link" /></a>}
                    {l.property?.id && (
                      <button className="text-xs text-primary shrink-0" title={t('desk_person_open_coop_tooltip')}
                        onClick={() => setCoopModal({ propertyId: l.property.id, propertyLabel: l.property.address,
                          seedIntent: coopIntentOf(l.intent), seedCurrency: l.currency || null, seedPrice: l.price ?? null })}>
                        🤝
                      </button>
                    )}
                  </div>
                ))}
                {/* desk_person_showing_of template uses {n} twice for two different values (shown, total) —
                    tLog's replaceAll would set both to the same number, so fill positionally instead. */}
                {data.listing_count > data.listings.length && (
                  <p className="text-xs text-muted-foreground pt-1">{t('desk_person_showing_of').replace('{n}', data.listings.length).replace('{n}', data.listing_count)}</p>
                )}
              </div>
            )}
          </div>

          {/* Cooperations (core/migrations/0046) — this party as the LISTING CONTACT.
              Discovery is not identity: a cooperation persists even if its source listing goes away. */}
          <div className={card}>
            <p className="text-xs font-semibold text-muted-foreground mb-2">
              {tLog('desk_person_cooperations', { n: coops.length })}
            </p>
            {coops.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('desk_person_no_cooperation')}</p>
            ) : (
              /* Same renderer as the listing-card chips — one interpretation of "a cooperation
                 of a property", many anchors (here: this party as the listing contact). */
              <CooperationChips cooperations={coops} privileged={privileged}
                onOpen={c => setCoopModal({ propertyId: c.property_id, cooperationId: c.id })} />
            )}
          </div>

          {/* Possible duplicates + confirmed identity cluster (core 0125) */}
          <PartyDuplicates partyId={partyId} agentId={agentId} privileged={privileged} />

          {/* Same footer shape as the listing + client modals (owner rule 2026-07-20):
              sticky, CONTACT bottom-left, actions right. The contact book card above lists
              every observation; this is the one-tap "call them" for the current primary. */}
          {/* flex-wrap + nowrap buttons: same mobile fix as the client modal footer. */}
          <div className="sticky bottom-0 -mx-5 -mb-5 px-5 py-2 mt-2 bg-card border-t border-border flex flex-wrap items-center justify-between gap-2 z-10">
            <span className="flex items-center gap-2 min-w-0">
              {/* SAME pill as the listing modal (owner rule: same exact design). Party mode
                  serves the whole 0141 contact book — every observation, ★ promote, ✕ mark
                  unreachable, + add — behind one logged reveal. Renders whenever the person
                  is unmasked and has anything to show (book rows count even when the
                  projected primary is empty). */}
              {(!data.contact_masked && ((data.phone_present ?? !!data.phone) || (data.email_present ?? !!data.email) || (data.contact_book || []).length > 0)) && (
                <ContactPill partyId={data.id || partyId} partyName={data.name}
                  label={t('desk_coop_chip_default')} />
              )}
            </span>
            <button className={`${btn('ghost')} whitespace-nowrap ml-auto`} onClick={onClose}
              title={t('desk_person_modal_close')}><BtnLabel icon="x" label={t('desk_person_modal_close')} /></button>
          </div>
        </div>
      )}
      {coopModal && (
        <CooperationModal
          propertyId={coopModal.propertyId}
          propertyLabel={coopModal.propertyLabel}
          cooperationId={coopModal.cooperationId}
          contactPartyId={partyId}
          contactName={data?.name}
          seedIntent={coopModal.seedIntent || null}
          seedCurrency={coopModal.seedCurrency || null}
          seedPrice={coopModal.seedPrice ?? null}
          onClose={() => setCoopModal(null)}
          onSaved={reloadCoops}
        />
      )}
    </Modal>
  )
}

export { demandLine, listingLine, PhonePersonHeader, PartyDuplicates, PersonModal }
